import React, { useMemo } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useLanguage } from '../../../contexts/LanguageContext';

const TrendChart = ({ tasks = [], expanded = false }) => {
  const { t } = useLanguage();
  const chartData = useMemo(() => {
    const today = new Date();
    const days = expanded ? 14 : 7;
    return Array.from({ length: days }, (_, i) => {
      const d = new Date(today);
      d?.setDate(d?.getDate() - (days - 1 - i));
      const dateStr = d?.toISOString()?.split('T')?.[0];
      const dayTasks = tasks?.filter(t => t?.date === dateStr);
      const completed = dayTasks?.filter(t => t?.status === 'completed')?.length;
      const total = dayTasks?.length;
      const rate = total > 0 ? Math.round((completed / total) * 100) : 0;
      return {
        date: d?.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric' }),
        rate,
        completed,
        total,
      };
    });
  }, [tasks, expanded]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-card border border-border rounded-xl shadow-elevation-2 p-3">
        <p className="text-xs font-semibold text-foreground mb-1">{label}</p>
        <p className="text-sm font-bold text-accent">{payload?.[0]?.value}% {t('completion')}</p>
        <p className="text-xs text-muted-foreground">{payload?.[0]?.payload?.completed}/{payload?.[0]?.payload?.total} {t('tasks')}</p>
      </div>
    );
  };

  return (
    <div className="bg-card rounded-xl border border-border shadow-elevation-2 overflow-hidden">
      <div className="px-5 py-4 border-b border-border">
        <h3 className="font-semibold text-foreground font-heading">{t('completionTrend')}</h3>
        <p className="text-xs text-muted-foreground mt-0.5">{expanded ? t('last14Days') : t('last7Days')} · {t('dailyCompletionRate')}</p>
      </div>
      <div className="p-5">
        <ResponsiveContainer width="100%" height={expanded ? 260 : 200}>
          <AreaChart data={chartData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="rateGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--color-accent)" stopOpacity={0.3} />
                <stop offset="95%" stopColor="var(--color-accent)" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
            <XAxis dataKey="date" tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} axisLine={false} tickLine={false} />
            <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} axisLine={false} tickLine={false} tickFormatter={v => `${v}%`} />
            <Tooltip content={<CustomTooltip />} />
            <Area type="monotone" dataKey="rate" stroke="var(--color-accent)" strokeWidth={2} fill="url(#rateGradient)" dot={{ fill: 'var(--color-accent)', strokeWidth: 0, r: 3 }} activeDot={{ r: 5 }} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default TrendChart;
